/**
 * The catalogs a tree needs: one per project any pane names (RFC 0281 S1b).
 *
 * `resolveSubject` made resolution a function of (catalog, id) rather than of
 * the window. This is the other half: the window has to be able to HOLD more
 * than one catalog, or a pure function that could read any project is still only
 * ever handed the one the shell happens to be on.
 *
 * WHY A MAP AND NOT A DOC. The shell used to keep `projectDoc` and close over
 * it. A map keyed by project id says the same thing for n projects, and it makes
 * the missing case explicit at the call site: a pane naming a project that has
 * not synced resolves to nothing and prunes, exactly as a pane naming a deleted
 * document does, rather than throwing on a doc that is not there yet.
 *
 * The read follows every document's progress query. A bulk `repo.find` gives
 * up when all sources first say unavailable, which is normal before the
 * desktop peer connects after a refresh; an empty catalog makes the shell
 * intentionally render the document pane as nothing.
 */

import type {
  AnyDocumentId,
  AutomergeUrl,
} from '@automerge/automerge-repo/slim'
import { type ProjectDoc, projectUrl } from '@mod/core/model'
import { useMemo } from 'react'
import { type ContentNode, leaves } from '../../features/code/contentTree'
import { withPrivateSessions } from '../../services/sessions/privateCatalog'
import { usePrivateSessions } from '../../services/sessions/usePrivateSessions'
import {
  useDocument,
  useDocuments,
} from '../../services/world/repo/useDocument'
import { readWorldPointer } from '../../services/world/world'
import { catalogOf, type ProjectCatalog } from '../window/resolveSubject'

/**
 * Every project id named anywhere in a tree, deduplicated and sorted.
 *
 * SORTED because it is a subscription key: `['a','b']` and `['b','a']` are the
 * same set of documents, and re-subscribing because two panes swapped places
 * would drop and refetch every catalog on a drag.
 *
 * Nulls are dropped rather than kept as a placeholder. A leaf with no project is
 * the cross project session list (`a` in the url grammar), which reads no
 * catalog at all, so there is nothing to fetch on its behalf.
 *
 * A TERMINAL has no project at all, not even a null one: it is catalogued on a
 * machine rather than on a project document (`TerminalContent`). So the field is
 * tested for presence, not just for truth, and a window showing only shells
 * fetches nothing.
 *
 * Pure, and exported for the test: the subscription set is the input to every
 * fetch this window makes, so getting it wrong is expensive in a way that is
 * hard to see from the outside.
 */
export function projectIdsOf(tree: ContentNode | null): string[] {
  const seen = new Set<string>()
  for (const leaf of leaves(tree)) {
    const { content } = leaf
    if ('projectId' in content && content.projectId) seen.add(content.projectId)
  }
  return [...seen].sort()
}

/**
 * Read one project DOCUMENT per id.
 *
 * TWO ID SPACES, CONVERTED ONCE. A leaf carries a bare `documentId`; the repo
 * speaks `automerge:<id>`. This hook used to straddle both by looking up each
 * key form in turn and taking whichever hit, which is not a fix — it agrees to
 * be wrong in one of two ways and absorbs the miss silently, so a genuine
 * absence and a key-shape mismatch become indistinguishable and a pane prunes
 * itself with no way to tell why.
 *
 * So the conversion happens ONCE, at the boundary, with mod-core's own
 * `projectUrl` (idempotent, and already the World's key space — a project is
 * a project). Subscribed by url, read back by url, and the map handed out is
 * keyed by the caller's bare id because that is what a leaf holds. One key form
 * on each side of the seam, and a miss is now a real miss.
 *
 * The raw document rather than a projection of it, because callers want
 * different projections: a pane wants apps and documents (`useProjectCatalogs`
 * below), while the sidebar runs `partitionSidebarItems` over the whole thing.
 * Projecting here would force every caller through one shape or make this hook
 * grow a union of everything anyone needs.
 */
export function useProjectDocs(projectIds: string[]): Map<string, ProjectDoc> {
  // A string key, because `useMemo` compares the array by identity and a fresh
  // array every render would rebuild the map every render.
  const key = projectIds.join(' ')
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed by content, not identity
  const ids = useMemo(() => projectIds, [key])

  /**
   * `suspense: false` is NOT optional here, and the default is `true`.
   *
   * With suspense on, `useDocHandles` THROWS a promise while any document is
   * still arriving, so this hook never returns a partial map — it returns
   * nothing at all until every project has synced. The catalog is then empty,
   * `resolveSubject` answers null, and `renderPane` bails: a document pane
   * renders as blank while a session pane, which needs no catalog, renders
   * fine. That is exactly the shape of the bug this comment exists to prevent
   * coming back.
   *
   * Off is also the honest setting for this call site. Everything downstream
   * already treats an absent project as "not arrived yet" rather than as an
   * error — `pruneMissing` waits on `catalogs.has(...)` before pruning anything
   * (§8's invariant) — so partial is a state this code understands and
   * suspending is not.
   */
  const urls = useMemo(() => ids.map((id) => projectUrl(id)), [ids])

  const docs = useDocuments<ProjectDoc>(urls as AnyDocumentId[])
  const privateSessions = usePrivateSessions()

  return useMemo(() => {
    const out = new Map<string, ProjectDoc>()
    if (privateSessions.enabled && privateSessions.loading) return out
    for (const id of ids) {
      const doc = docs.get(projectUrl(id) as AutomergeUrl)
      if (doc)
        out.set(
          id,
          privateSessions.enabled
            ? withPrivateSessions(
                doc,
                id,
                privateSessions.entries,
                privateSessions.shared,
              )
            : doc,
        )
    }
    return out
  }, [
    ids,
    docs,
    privateSessions.enabled,
    privateSessions.loading,
    privateSessions.entries,
    privateSessions.shared,
  ])
}

/** One catalog per project id, for the panes that resolve against them. */
export function useProjectCatalogs(
  projectIds: string[],
): Map<string, ProjectCatalog> {
  const docs = useProjectDocs(projectIds)
  // The World's apps open records in every project (RFC 0338 3g).
  const worldId = readWorldPointer() ?? undefined
  const [worldDoc] = useDocument(worldId as AnyDocumentId | undefined)
  return useMemo(() => {
    const out = new Map<string, ProjectCatalog>()
    // NO CATALOG until the World has loaded, when there is one. Without its
    // apps a World app's record resolves as having none and draws the install
    // prompt (RFC 0338 S7). An absent catalog is "not arrived yet", which every
    // pane already waits on rather than pruning.
    if (worldId && !worldDoc) return out
    for (const [id, doc] of docs) out.set(id, catalogOf(doc, worldDoc))
    return out
  }, [docs, worldDoc, worldId])
}
